import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import SearchBar from "@/components/SearchBar";
import CategoryFilter from "@/components/CategoryFilter";
import ToolCard from "@/components/ToolCard";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import type { Tool } from "@/types/tool";

interface ToolsSectionProps {
  searchQuery: string;
  selectedCategory: string | null;
  onSearchChange: (value: string) => void;
  onCategorySelect: (category: string | null) => void;
  featuredTools: Tool[];
  regularTools: Tool[];
  onBusinessNameClick?: (lat: number | undefined, lng: number | undefined) => void;
}

const ToolsSection = ({
  searchQuery,
  selectedCategory,
  onSearchChange,
  onCategorySelect,
  featuredTools,
  regularTools,
  onBusinessNameClick,
}: ToolsSectionProps) => {
  const filterTools = (tools: Tool[]) => {
    return tools.filter((tool) => {
      const matchesSearch = !searchQuery || 
        tool.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tool.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tool.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));

      const matchesCategory = !selectedCategory || 
        tool.categories.some(category => 
          category.toLowerCase() === selectedCategory.toLowerCase()
        );

      return matchesSearch && matchesCategory;
    });
  };

  const handleTagClick = (tag: string) => {
    onCategorySelect(tag.toLowerCase());
  };

  const filteredFeaturedTools = filterTools(featuredTools);
  const filteredRegularTools = filterTools(regularTools);

  return (
    <Card className="bg-white/90 backdrop-blur-md shadow-xl border-0 transition-all duration-500">
      <CardHeader className="p-6 space-y-5 bg-gradient-to-br from-purple-50/50 via-white to-purple-50/50 border-b border-purple-100/30">
        <SearchBar value={searchQuery} onChange={onSearchChange} />
        
        <Sheet>
          <SheetTrigger asChild>
            <Button 
              variant="outline" 
              className="lg:hidden w-full bg-white/90 backdrop-blur-sm hover:bg-purple-50 border-purple-200/50 text-purple-700 shadow-sm"
            >
              Filters
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[300px] sm:w-[400px]">
            <div className="py-4">
              <CategoryFilter
                selectedCategory={selectedCategory}
                onSelectCategory={onCategorySelect}
              />
            </div>
          </SheetContent>
        </Sheet>

        <div className="hidden lg:block">
          <CategoryFilter
            selectedCategory={selectedCategory}
            onSelectCategory={onCategorySelect}
          />
        </div>
      </CardHeader>

      <CardContent className="p-6">
        <div className="space-y-8">
          {filteredFeaturedTools.length > 0 && (
            <div>
              <h2 className="text-2xl font-semibold bg-clip-text text-transparent bg-gradient-to-r from-purple-700 to-purple-900 mb-6">
                Featured Professionals
              </h2>
              <div className="space-y-6">
                {filteredFeaturedTools.map((tool) => (
                  <ToolCard 
                    key={tool.id} 
                    tool={tool} 
                    onBusinessNameClick={onBusinessNameClick}
                    onTagClick={handleTagClick}
                  />
                ))}
              </div>
            </div>
          )}

          <div>
            <h2 className="text-2xl font-semibold bg-clip-text text-transparent bg-gradient-to-r from-purple-700 to-purple-900 mb-6">
              All Professionals
            </h2>
            <div className="space-y-6">
              {filteredRegularTools.map((tool) => (
                <ToolCard 
                  key={tool.id} 
                  tool={tool} 
                  onBusinessNameClick={onBusinessNameClick}
                  onTagClick={handleTagClick}
                />
              ))}
            </div>

            {filteredRegularTools.length === 0 && (
              <div className="text-center py-12 bg-gradient-to-br from-purple-50/50 via-white to-purple-50/50 rounded-xl border border-purple-100/30 shadow-inner">
                <p className="text-purple-600/90 font-medium">No professionals found matching your criteria.</p>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ToolsSection;